import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Register.css';
import { useAuth } from '../../contexts/AuthContext';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword, deleteUser, signOut, updateProfile } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

function RegisterPage() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [registrationWarning, setRegistrationWarning] = useState(null);
    const [isVerifying, setIsVerifying] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateStep1 = () => {
        const newErrors = {};
        
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};
        
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }
        
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Check if user is in the authorized list
    const checkUserAuthorization = async (user) => {
        try {
            if (!user || !user.email) {
                console.error('Invalid user object:', user);
                return {
                    authorized: false,
                    message: 'Registration failed. Please try again.'
                };
            }

            console.log('Checking authorization for user email:', user.email);
            // Use email as document ID, collection name is 'Users' (uppercase)
            const userDocRef = doc(db, 'Users', user.email);
            const userDoc = await getDoc(userDocRef);
            
            console.log('User document exists:', userDoc.exists());
            
            // If user document doesn't exist, this email is not in the allowed system list
            if (!userDoc.exists()) {
                return {
                    authorized: false,
                    message: 'This email address is not registered in our system.'
                };
            }
            
            // If user document exists, check user data
            const userData = userDoc.data();
            console.log('User data:', userData);
            
            // Additional validation
            if (userData.status === 'pending' || userData.status === 'inactive') {
                return {
                    authorized: false,
                    message: 'Your account is pending approval.'
                };
            }
            
            // If user document exists and is not disabled, authorization successful
            return { authorized: true, userData };
        } catch (error) {
            console.error('Error checking user authorization:', error);
            return {
                authorized: false,
                message: 'Unable to verify your email address.'
            };
        }
    };

    const handleNextStep = async (e) => {
        e.preventDefault();
        
        if (currentStep === 1) {
            if (validateStep1()) {
                setCurrentStep(2);
            }
        } else if (currentStep === 2) {
            if (validateStep2()) {
                setIsLoading(true);
                
                try {
                    console.log('Creating user account...');
                    // Use Firebase Auth to create user directly
                    const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
                    console.log('User created:', userCredential.user.uid);
                    
                    // Jump to verification page
                    setCurrentStep(3);
                    setIsLoading(false);
                    setIsVerifying(true);
                    
                    // Start verification process
                    await startVerificationProcess(userCredential.user);
                    
                } catch (error) {
                    console.error('Registration error:', error);
                    
                    // Handle specific error messages
                    let errorMessage = 'Registration failed.';
                    switch (error.code) {
                        case 'auth/email-already-in-use':
                            errorMessage = 'An account with this email already exists';
                            break;
                        case 'auth/invalid-email':
                            errorMessage = 'Invalid email address';
                            break;
                        case 'auth/weak-password':
                            errorMessage = 'Password should be at least 6 characters';
                            break;
                        case 'auth/operation-not-allowed':
                            errorMessage = 'Email/password accounts are not enabled';
                            break;
                        default:
                            errorMessage = error.message || errorMessage;
                    }
                    
                    setErrors({ general: errorMessage });
                } finally {
                    setIsLoading(false);
                }
            }
        }
    };

    // Verification process
    const startVerificationProcess = async (user) => {
        try {
            // Perform user authorization check
            const authCheck = await checkUserAuthorization(user);
            
            if (!authCheck.authorized) {
                // Delete unauthorized user account
                try {
                    await deleteUser(user);
                    await signOut(auth);
                    console.log('Unauthorized user account deleted');
                } catch (deleteError) {
                    console.error('Error deleting unauthorized user:', deleteError);
                    // Still try to sign out
                    try {
                        await signOut(auth);
                    } catch (signOutError) {
                        console.error('Error signing out:', signOutError);
                    }
                }
                
                // Clear user state and show error
                setUser(null);
                setIsVerifying(false);
                setRegistrationWarning(authCheck.message);
                setCurrentStep(5);
                return;
            }
            
            // Authorization successful
            console.log('User authorized successfully');
            
            // Update Firebase Auth displayName with the name from Firestore
            try {
                if (authCheck.userData && authCheck.userData.name) {
                    await updateProfile(user, {
                        displayName: authCheck.userData.name
                    });
                    console.log('User displayName updated to:', authCheck.userData.name);
                }
            } catch (updateError) {
                console.error('Error updating user displayName:', updateError);
            }
            
            // Set authenticated user and navigate to home
            setUser(user);
            setIsVerifying(false);
            navigate('/home');
            
        } catch (error) {
            console.error('Verification process error:', error);
            
            try {
                if (user) {
                    await deleteUser(user);
                    await signOut(auth);
                    console.log('User account deleted due to verification error');
                }
            } catch (deleteError) {
                console.error('Error deleting user after verification error:', deleteError);
                try {
                    await signOut(auth);
                } catch (signOutError) {
                    console.error('Error signing out after delete failure:', signOutError);
                }
            }
            
            setUser(null);
            setIsVerifying(false);
            setRegistrationWarning('Verification failed. Please try again or contact support.');
            setCurrentStep(5);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const navigateToLogin = () => {
        navigate('/login');
    };

    const goToLogin = () => {
        navigate('/login');
    };

    const handleTryAgain = () => {
        setCurrentStep(1);
        setRegistrationWarning(null);
        setFormData({
            email: '',
            password: '',
            confirmPassword: ''
        });
        setErrors({});
        setIsVerifying(false);
    };

    // Registration failure page (Step 5)
    if (currentStep === 5 && registrationWarning) {
        return (
            <div className="register-failure-container">
                <div className="register-failure-card">
                    <h1 className="register-failure-title">Registration Failed</h1>
                    <div className="register-failure-content">
                        <p>{registrationWarning}</p>
                        <div className="failure-suggestions">
                            <h3>What you can do:</h3>
                            <ul>
                                <li>Use your school email address (e.g., @uci.edu)</li>
                                <li>Contact your instructor to add your email to the system</li>
                                <li>Check if you received an invitation email</li>
                            </ul>
                        </div>
                    </div>
                    <div className="failure-buttons">
                        <button onClick={handleTryAgain} className="failure-retry-button">
                            Try Again
                        </button>
                        <button onClick={goToLogin} className="failure-login-button">
                            Go to Login
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Account verification page (Step 3)
    // if (currentStep === 3 && isVerifying) {
    //     return (
    //         <div className="register-verification-container">
    //             <div className="register-verification-card">
    //                 <div className="verification-icon">
    //                     <div className="spinner"></div>
    //                 </div>
    //                 <h1 className="register-verification-title">Verifying Registration</h1>
    //                 <div className="register-verification-content">
    //                     <p>Please wait while we verify your account...</p>
                        
    //                     <div className="verification-progress">
    //                         <div className="progress-bar">
    //                             <div 
    //                                 className="progress-fill" 
    //                                 style={{ width: `${verificationProgress}%` }}
    //                             ></div>
    //                         </div>
    //                         <div className="progress-text">{verificationProgress}%</div>
    //                     </div>
                        
    //                     {/* <div className="verification-steps">
    //                         <div className={`verification-step ${verificationProgress >= 20 ? 'completed' : ''}`}>
    //                             <span className="step-icon">✓</span>
    //                             Account created
    //                         </div>
    //                         <div className={`verification-step ${verificationProgress >= 40 ? 'completed' : ''}`}>
    //                             <span className="step-icon">✓</span>
    //                             Email verified
    //                         </div>
    //                         <div className={`verification-step ${verificationProgress >= 60 ? 'completed' : ''}`}>
    //                             <span className="step-icon">✓</span>
    //                             Permissions checked
    //                         </div>
    //                         <div className={`verification-step ${verificationProgress >= 80 ? 'completed' : ''}`}>
    //                             <span className="step-icon">✓</span>
    //                             Credentials validated
    //                         </div>
    //                         <div className={`verification-step ${verificationProgress >= 100 ? 'completed' : ''}`}>
    //                             <span className="step-icon">✓</span>
    //                             Registration finalized
    //                         </div>
    //                     </div> */}
    //                 </div>
    //             </div>
    //         </div>
    //     );
    // }

    return (
        <div className="register-container">
            <div className="register-body">
                <div className="register-image-section">
                    <img src="/images/UCI_Donald_Bren_Hall.jpg" alt="Donald Bren Hall" />
                </div>
                <div className="register-form-section">
                    <div className="register-content">
                        <h1 className="register-title">Join TeamCAST</h1>
                        <p className="register-subtitle">Create your collaboration account</p>
                        
                        {/* Progress indicator */}
                        <div className="register-progress">
                            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>1</div>
                            <div className="progress-arrow">→</div>
                            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>2</div>
                            <div className="progress-arrow">→</div>
                            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
                        </div>
                        
                        <form onSubmit={handleNextStep} className="register-form">
                            {currentStep === 1 && (
                                <div className="form-group">
                                    <label htmlFor="email">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`form-input ${errors.email ? 'error' : ''}`}
                                        placeholder=""
                                    />
                                    {errors.email && <span className="error-message">{errors.email}</span>}
                                    <div className="email-hint">
                                        <p>Please use your school email address</p>
                                    </div>
                                </div>
                            )}

                            {currentStep === 2 && (
                                <>
                                    <div className="form-group">
                                        <label htmlFor="password">Password</label>
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`form-input ${errors.password ? 'error' : ''}`}
                                            placeholder=""
                                        />
                                        {errors.password && <span className="error-message">{errors.password}</span>}
                                    </div>
                                    
                                    <div className="form-group">
                                        <label htmlFor="confirmPassword">Confirm Password</label>
                                        <input
                                            type="password"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                                            placeholder=""
                                        />
                                        {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                                    </div>
                                </>
                            )}
                            
                            {errors.general && <div className="error-message general-error">{errors.general}</div>}
                            
                            <div className="register-buttons">
                                {currentStep === 2 && (
                                    <button 
                                        type="button" 
                                        onClick={handleBack}
                                        className="register-back-button"
                                    >
                                        Back
                                    </button>
                                )}
                                <button 
                                    type="submit" 
                                    className="register-next-button"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Creating Account...' : 'Next Step'}
                                </button>
                            </div>
                        </form>

                        {/* Sign In Link */}
                        <div className="signin-section">
                            <p>Already have an account? <button onClick={navigateToLogin} className="signin-link">Sign In here</button></p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RegisterPage;